import React, { useState, useEffect } from 'react';
import { X, Mail, Clock, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';

const API = process.env.REACT_APP_BACKEND_URL;

export function BetaPopup() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    const dismissed = sessionStorage.getItem('beta_popup_dismissed');
    if (!dismissed) {
      const timer = setTimeout(() => setOpen(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleDismiss = () => {
    setOpen(false);
    sessionStorage.setItem('beta_popup_dismissed', 'true');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      const data = await res.json();
      if (res.ok) {
        setSubmitted(true);
        setMessage(data.message);
      } else {
        setMessage(data.detail || 'Something went wrong. Please try again.');
      }
    } catch {
      setMessage('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="beta-popup-overlay">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleDismiss} />
      <div
        className="relative w-full max-w-md rounded-2xl border border-dp-500/30 bg-slate-900 p-8 shadow-2xl shadow-dp-900/50 animate-in fade-in zoom-in-95 duration-300"
        data-testid="beta-popup"
      >
        <button
          onClick={handleDismiss}
          className="absolute top-4 right-4 text-slate-500 hover:text-slate-300 transition-colors"
          data-testid="beta-popup-close"
        >
          <X className="w-5 h-5" />
        </button>

        {!submitted ? (
          <>
            <div className="flex items-center gap-2 mb-4">
              <div className="px-3 py-1 rounded-full bg-amber-500/20 border border-amber-500/30 text-amber-400 text-xs font-bold uppercase tracking-wider">
                Beta
              </div>
              <div className="flex items-center gap-1 text-slate-500 text-xs">
                <Clock className="w-3 h-3" />
                Launching May 2026
              </div>
            </div>

            <h2 className="text-2xl font-bold text-white mb-2">
              We're almost ready!
            </h2>
            <p className="text-slate-400 text-sm leading-relaxed mb-6">
              DentalPulse is currently in beta testing and not yet available for purchase.
              Join the waitlist to be the first to know when we go live.
            </p>

            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@yourpractice.com"
                  required
                  data-testid="beta-email-input"
                  className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-dp-500 focus:ring-dp-500/20"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                data-testid="beta-submit-btn"
                className="w-full bg-gradient-to-r from-dp-500 to-dp-600 hover:from-dp-400 hover:to-dp-500 text-white font-semibold py-5 rounded-xl"
              >
                {loading ? 'Joining...' : 'Notify Me When It\u2019s Live'}
                {!loading && <ArrowRight className="w-4 h-4 ml-2" />}
              </Button>
            </form>

            {message && !submitted && (
              <p className="mt-3 text-sm text-red-400 text-center">{message}</p>
            )}

            <p className="text-xs text-slate-600 text-center mt-4">
              No spam. Just one email when we launch.
            </p>
          </>
        ) : (
          <div className="text-center py-4" data-testid="beta-success">
            <div className="w-16 h-16 rounded-full bg-dp-500/20 flex items-center justify-center mx-auto mb-4">
              <Mail className="w-8 h-8 text-dp-300" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">You're on the list!</h2>
            <p className="text-slate-400 text-sm leading-relaxed mb-6">
              {message}
            </p>
            <Button
              onClick={handleDismiss}
              data-testid="beta-success-close"
              className="bg-dp-600 hover:bg-dp-700 text-white px-8"
            >
              Got it
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
